import React, { useState, useEffect, useCallback, useRef, useImperativeHandle, forwardRef } from 'react';
import MultiSelectTicker from '../MultiSelectTicker';
import AccordionFormSection from '../Screener/AccordionFormSection';
import IntermediatePresetCard from '../Screener/IntermediatePresetCard';
import TargetPriceStrikeSection from './TargetPriceStrikeSection';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import {
  intradayPresets,
  buildIntradayScenarios,
  buildZeroDTETargetScenarios,
} from '../../utils/presetMappings';
import { getOptionsChain } from '../../services/api';
import { cardToggleTemplate, actionButtonTemplate } from '../../theme';
import { Play } from 'lucide-react';

// 0DTE module form — short-dated (0DTE → ~3 months), ONE ticker at a time.
// Mirrors the Intermediate form's structure (Select Ticker + 4-category
// direction grid + accordion + queue/run/save buttons) but reads the
// short-dated intradayPresets and emits scenarios via buildIntradayScenarios
// (which carries the explicit expiration window + _intraday metadata).
// 0DTE analyses one ticker at a time — the underlying price anchors both the
// target-price strike mode and the price shown beside each scenario column.
// Picking another ticker replaces the current one rather than adding to it.
export const capToOneTicker = (tickers) =>
  Array.isArray(tickers) && tickers.length > 0 ? [tickers[tickers.length - 1]] : [];

const ZeroDTEForm = forwardRef(({
  onAddToQueue,
  onAddAndRun,
  onSaveScenario,
  queueCount,
  loading,
  hideButtons = false,
  onFormValidityChange,
  // Reports the ticker + target-price context upward so the page's chart card
  // can mirror it. Must be referentially stable (useCallback) — the emit effect
  // below depends on it.
  onTargetContextChange = null
}, ref) => {
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Form values
  const [selectedTickers, setSelectedTickers] = useState([]);
  const [direction, setDirection] = useState(null); // 'call' or 'put'
  const [selectedExpirations, setSelectedExpirations] = useState([]);
  const [selectedStrikes, setSelectedStrikes] = useState([]);

  // Strike positioning sub-mode: moneyness bands (default) or an absolute
  // target price with a buffer around it.
  const [strikeMode, setStrikeMode] = useState('bands'); // 'bands' | 'target'
  const [targetPrice, setTargetPrice] = useState('');
  const [bufferPct, setBufferPct] = useState(null); // null = exact target
  const [currentPrice, setCurrentPrice] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceError, setPriceError] = useState(null);

  // Accordion state
  const [expandedSections, setExpandedSections] = useState(
    isMobile ? ['tickers'] : ['tickers', 'direction', 'expiration', 'strike']
  );
  const [completedSections, setCompletedSections] = useState(new Set());
  const [activeSection, setActiveSection] = useState('tickers');

  const activeTicker = selectedTickers.length > 0 ? selectedTickers[0].value : null;

  // loadScenario restores a ticker and its target price together; the
  // ticker-change effect below must not wipe the target it just set.
  const pendingTargetRef = useRef(null);
  // The ticker we last fired a quote request for — stops a failed fetch from
  // retrying in a loop, and self-invalidates when the ticker changes.
  const attemptedTickerRef = useRef(null);

  const fetchCurrentPrice = useCallback(async (ticker) => {
    if (!ticker) return;
    setPriceLoading(true);
    setPriceError(null);
    try {
      const data = await getOptionsChain(ticker);
      const price = parseFloat(data?.currentPrice);
      if (Number.isFinite(price) && price > 0) {
        setCurrentPrice(price);
      } else {
        setCurrentPrice(null);
        setPriceError('unavailable');
      }
    } catch (err) {
      setCurrentPrice(null);
      setPriceError(err?.message || 'failed');
    } finally {
      setPriceLoading(false);
    }
  }, []);

  // A different underlying invalidates both the quote and any target derived
  // from it — a $660 target means nothing once you switch from SPY to NVDA.
  useEffect(() => {
    setCurrentPrice(null);
    setPriceError(null);
    if (pendingTargetRef.current != null) {
      setTargetPrice(pendingTargetRef.current);
      pendingTargetRef.current = null;
    } else {
      setTargetPrice('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTicker]);

  // Only the target-price mode needs a live quote, so bands users never pay for
  // the request.
  useEffect(() => {
    if (strikeMode !== 'target' || !activeTicker) return;
    if (attemptedTickerRef.current === activeTicker) return;
    attemptedTickerRef.current = activeTicker;
    fetchCurrentPrice(activeTicker);
  }, [strikeMode, activeTicker, fetchCurrentPrice]);

  const handleRetryPrice = useCallback(() => {
    attemptedTickerRef.current = activeTicker;
    fetchCurrentPrice(activeTicker);
  }, [activeTicker, fetchCurrentPrice]);

  // Mirror the target context up to the page, which renders the chart card in
  // the right panel. A ticker change flushes immediately so the card opens
  // without lag; value edits are debounced so typing a target price doesn't
  // re-render the page (and the results table below it) on every keystroke.
  const emittedCtxRef = useRef(null);
  useEffect(() => {
    if (!onTargetContextChange) return;
    const ctx = {
      ticker: activeTicker,
      strikeMode,
      targetPrice,
      bufferPct,
      currentPrice,
      priceLoading,
      priceError,
    };
    const tickerChanged = emittedCtxRef.current?.ticker !== ctx.ticker;
    emittedCtxRef.current = ctx;
    if (tickerChanged) {
      onTargetContextChange(ctx);
      return;
    }
    const timer = setTimeout(() => onTargetContextChange(emittedCtxRef.current), 150);
    return () => clearTimeout(timer);
  }, [activeTicker, strikeMode, targetPrice, bufferPct, currentPrice,
      priceLoading, priceError, onTargetContextChange]);

  // Expose form state and methods to parent component
  useImperativeHandle(ref, () => ({
    getFormState: () => ({
      selectedTickers,
      direction,
      selectedExpirations,
      selectedStrikes,
      strikeMode,
      targetPrice,
      bufferPct,
      isValid: isFormValid()
    }),
    buildScenarios,
    isFormValid,
    resetForm: () => {
      setSelectedTickers([]);
      setDirection(null);
      setSelectedExpirations([]);
      setSelectedStrikes([]);
      setStrikeMode('bands');
      setTargetPrice('');
      setBufferPct(null);
      setCompletedSections(new Set());
      setActiveSection('tickers');
      setExpandedSections(isMobile ? ['tickers'] : ['tickers', 'direction', 'expiration', 'strike']);
    },
    // Guided-tour demo population. Applies `value` to `part` ONLY when that
    // part is currently empty — never overwrites the user's own selections.
    tourPopulate: (part, value) => {
      if (part === 'tickers' && selectedTickers.length === 0 && Array.isArray(value)) {
        setSelectedTickers(capToOneTicker(value.map(t => ({ value: t, label: t }))));
        setCompletedSections(prev => new Set(prev).add('tickers'));
      } else if (part === 'direction' && !direction && value) {
        setDirection(value);
        setCompletedSections(prev => new Set(prev).add('direction'));
      } else if (part === 'expiration' && selectedExpirations.length === 0 && Array.isArray(value)) {
        setSelectedExpirations(value.filter(k => intradayPresets.expiration[k]));
        setCompletedSections(prev => new Set(prev).add('expiration'));
      } else if (part === 'strike' && selectedStrikes.length === 0 && Array.isArray(value)) {
        setSelectedStrikes(value.filter(k => intradayPresets.strike[k]));
        setCompletedSections(prev => new Set(prev).add('strike'));
      }
    },
    // Chart click-to-pick. Deliberately does NOT set pendingTargetRef: the
    // ticker isn't changing, so the clear-on-ticker-change effect won't fire and
    // can't wipe this. Switching to target mode also arms the quote fetch, which
    // target-mode validity needs.
    setTargetFromChart: (price) => {
      if (!Number.isFinite(price) || price <= 0) return;
      setStrikeMode('target');
      setTargetPrice(price.toFixed(2));
      setCompletedSections(prev => new Set(prev).add('strike'));
      setExpandedSections(prev => (prev.includes('strike') ? prev : [...prev, 'strike']));
    },
    loadScenario: (scenario) => {
      console.log('Loading scenario into 0DTE form:', scenario);

      // Set ticker (0DTE analyses one at a time — a multi-ticker legacy
      // scenario collapses to its last entry).
      const tickerArray = Array.isArray(scenario.tickers)
        ? scenario.tickers
        : scenario.tickers.split(',').map(t => t.trim());

      const tickerObjects = tickerArray.map(ticker => ({
        value: ticker,
        label: ticker
      }));
      setSelectedTickers(capToOneTicker(tickerObjects));

      // Set direction - handle both legacy (option_type only) and new (position_type + option_type) formats
      const positionType = scenario.position_type || 'buy'; // Default to 'buy' for legacy scenarios
      const optionType = scenario.option_type; // 'call' or 'put'
      setDirection(`${positionType}_${optionType}`); // e.g., 'buy_call', 'sell_put'

      // Try to match to intraday presets if _intraday metadata exists
      if (scenario._intraday?.strikeMode === 'target') {
        setSelectedExpirations([scenario._intraday.expiration]);
        setStrikeMode('target');
        setBufferPct(scenario._intraday.bufferPct ?? null);
        // Survives the ticker-change effect that would otherwise clear it.
        pendingTargetRef.current = String(scenario._intraday.targetPrice ?? '');
        setTargetPrice(String(scenario._intraday.targetPrice ?? ''));
      } else if (scenario._intraday) {
        setSelectedExpirations([scenario._intraday.expiration]);
        setSelectedStrikes([scenario._intraday.strike]);
        setStrikeMode('bands');
      } else {
        // Try to find closest match based on parameters
        const expMatch = findClosestExpiration(scenario.days_until_exp);
        const strikeMatch = findClosestStrike(scenario.strike_pct, scenario.option_type);

        if (expMatch) setSelectedExpirations([expMatch]);
        if (strikeMatch) setSelectedStrikes([strikeMatch]);
      }

      // Mark sections as completed
      setCompletedSections(new Set(['tickers', 'direction', 'expiration', 'strike']));

      // Expand appropriate sections
      if (isMobile) {
        setExpandedSections(['strike']);
        setActiveSection('strike');
      } else {
        setExpandedSections(['tickers', 'direction', 'expiration', 'strike']);
      }
    }
  }));

  // Helper functions to find closest preset matches
  const findClosestExpiration = (daysUntilExp) => {
    let closest = null;
    let minDiff = Infinity;
    for (const [key, preset] of Object.entries(intradayPresets.expiration)) {
      const diff = Math.abs(preset.days_until_exp - daysUntilExp);
      if (diff < minDiff) {
        minDiff = diff;
        closest = key;
      }
    }
    return closest;
  };

  const findClosestStrike = (strikePct, optionType) => {
    // For puts, the strike was already adjusted, so we need to un-adjust
    const adjustedStrikePct = optionType === 'put' && strikePct !== 0 ? -strikePct : strikePct;

    let closest = null;
    let minDiff = Infinity;
    for (const [key, preset] of Object.entries(intradayPresets.strike)) {
      const diff = Math.abs(preset.strike_pct - adjustedStrikePct);
      if (diff < minDiff) {
        minDiff = diff;
        closest = key;
      }
    }
    return closest;
  };

  // Unified function to handle section transitions (mobile)
  const activateNextSection = (currentSection, nextSection = null) => {
    if (!isMobile) return;

    setCompletedSections(prev => new Set(prev).add(currentSection));

    if (nextSection) {
      setExpandedSections([nextSection]);
      setActiveSection(nextSection);
    } else {
      setExpandedSections([]);
      setActiveSection('complete');
    }
  };

  // Auto-advance logic (mobile only)
  useEffect(() => {
    if (!isMobile) return;
    if (selectedTickers.length > 0 && activeSection === 'tickers') {
      activateNextSection('tickers', 'direction');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTickers, isMobile]);

  useEffect(() => {
    if (!isMobile) return;
    if (direction && activeSection === 'direction') {
      activateNextSection('direction', 'expiration');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direction, isMobile]);

  useEffect(() => {
    if (!isMobile) return;
    if (selectedExpirations.length > 0 && activeSection === 'expiration') {
      activateNextSection('expiration', 'strike');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedExpirations, isMobile]);

  useEffect(() => {
    if (!isMobile) return;
    if (selectedStrikes.length > 0 && activeSection === 'strike') {
      setCompletedSections(prev => new Set(prev).add('strike'));
      setTimeout(() => {
        setExpandedSections([]);
        setActiveSection('complete');
      }, 300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStrikes, isMobile]);

  // Monitor form validity and notify parent
  useEffect(() => {
    if (onFormValidityChange) {
      onFormValidityChange(isFormValid());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTickers, direction, selectedExpirations, selectedStrikes, strikeMode, targetPrice, currentPrice, onFormValidityChange]);

  // Helper: Determine if section should be disabled (mobile only)
  const isSectionDisabled = (section) => {
    if (!isMobile) return false;

    if (completedSections.has(section)) return false;

    const sectionOrder = ['tickers', 'direction', 'expiration', 'strike'];
    const sectionIndex = sectionOrder.indexOf(section);

    let highestCompletedIndex = -1;
    sectionOrder.forEach((s, idx) => {
      if (completedSections.has(s)) {
        highestCompletedIndex = Math.max(highestCompletedIndex, idx);
      }
    });

    return sectionIndex > highestCompletedIndex + 1;
  };

  // Handle section toggle
  const handleToggle = (section) => {
    if (isMobile) {
      setExpandedSections(prev => {
        if (prev.includes(section)) {
          return [];
        } else {
          return [section];
        }
      });

      const sectionOrder = ['tickers', 'direction', 'expiration', 'strike'];
      if (sectionOrder.includes(section)) {
        setActiveSection(section);
      }
    } else {
      setExpandedSections(prev =>
        prev.includes(section)
          ? prev.filter(s => s !== section)
          : [...prev, section]
      );
    }
  };

  // Target mode needs a valid target AND a live quote to convert it into the
  // moneyness the backend expects.
  const isTargetStrikeReady = () => {
    const target = parseFloat(targetPrice);
    return Number.isFinite(target) && target > 0 && Number.isFinite(currentPrice) && currentPrice > 0;
  };

  // Check if form is valid
  const isFormValid = () => {
    const strikeReady = strikeMode === 'target' ? isTargetStrikeReady() : selectedStrikes.length > 0;
    return (
      selectedTickers.length > 0 &&
      direction !== null &&
      selectedExpirations.length > 0 &&
      strikeReady
    );
  };

  // Generate tooltip message for disabled button
  const getDisabledTooltipMessage = () => {
    const missingItems = [];

    if (selectedTickers.length === 0) missingItems.push('Ticker');
    if (direction === null) missingItems.push('Direction');
    if (selectedExpirations.length === 0) missingItems.push('Expiration');
    if (strikeMode === 'target') {
      if (!isTargetStrikeReady()) missingItems.push('Target Price');
    } else if (selectedStrikes.length === 0) {
      missingItems.push('Strike');
    }

    if (missingItems.length === 0) return '';
    if (missingItems.length === 1) return `Please select ${missingItems[0]}`;
    if (missingItems.length === 2) return `Please select ${missingItems[0]} and ${missingItems[1]}`;
    return `Please complete all sections`;
  };

  // Build scenarios for API (all combinations)
  // Exit Speed and Stock Move are seeded (not asked) for the same reasons as the
  // Intermediate form — the backend recalculates days_to_gain per-contract and
  // the post-results Elasticity Heatmap is where short-dated scenarios are
  // actually explored. Direction-aware stock-move seed:
  // bullish positions → +20% (rally); bearish positions → -20% (correction).
  const buildScenarios = () => {
    if (!isFormValid()) return [];

    if (strikeMode === 'target') {
      return buildZeroDTETargetScenarios(
        {
          expirations: selectedExpirations,
          targetPrice,
          bufferPct,
          currentPrice,
        },
        selectedTickers,
        direction
      );
    }

    const isBullishPosition = direction === 'buy_call' || direction === 'sell_put';
    const moveSeed = isBullishPosition ? 'rally' : 'correction';

    const scenarios = buildIntradayScenarios(
      {
        expirations: selectedExpirations,
        strikes: selectedStrikes,
        speeds: ['balanced'],
        moves: [moveSeed]
      },
      selectedTickers,
      direction
    );

    return scenarios;
  };

  // Handle submit (add all scenarios to queue)
  const handleSubmit = () => {
    const scenarios = buildScenarios();
    if (scenarios.length > 0 && onAddToQueue) {
      scenarios.forEach(scenario => onAddToQueue(scenario));
    }
  };

  // Run immediately — append the form's scenarios to the queue AND fire the
  // screener in one tap (skips the "review queue, then run" step).
  const handleRunNow = () => {
    const scenarios = buildScenarios();
    if (scenarios.length > 0 && onAddAndRun) {
      onAddAndRun(scenarios);
    }
  };

  // Toggle handlers for multi-select
  const toggleSelection = (array, setArray, key) => {
    setArray(prev => {
      if (prev.includes(key)) {
        return prev.filter(k => k !== key);
      } else {
        return [...prev, key];
      }
    });
  };

  // Determine if current direction is a put option
  const isPut = direction?.includes('put');

  // Get adjusted strike description for puts (invert the sign)
  const getAdjustedStrikeDescription = (description) => {
    if (!isPut || !description) return description;
    // For puts, invert the percentage sign in the description
    // e.g., "+1–2%" becomes "-1–2%", "-2–-5%" becomes "+2–-5%"
    if (description.startsWith('-')) {
      return '+' + description.slice(1);
    } else if (description.startsWith('+')) {
      return '-' + description.slice(1);
    }
    return description;
  };

  // Get summary text for accordion headers
  const getExpirationSummary = () => {
    if (selectedExpirations.length === 0) return '';
    if (selectedExpirations.length === 1) {
      return intradayPresets.expiration[selectedExpirations[0]]?.label || '';
    }
    return `${selectedExpirations.length} selected`;
  };

  const getStrikeSummary = () => {
    if (strikeMode === 'target') {
      const target = parseFloat(targetPrice);
      if (!Number.isFinite(target) || target <= 0) return '';
      return bufferPct != null
        ? `Target $${target} ±${+(bufferPct * 100).toFixed(2)}%`
        : `Target $${target}`;
    }
    if (selectedStrikes.length === 0) return '';
    if (selectedStrikes.length === 1) {
      const preset = intradayPresets.strike[selectedStrikes[0]];
      const adjustedDesc = getAdjustedStrikeDescription(preset?.description);
      return `${preset?.label || ''} ${adjustedDesc || ''}`.trim();
    }
    return `${selectedStrikes.length} selected`;
  };

  // Calculate total scenarios for button text. Target mode emits one scenario
  // per expiration (the strike set comes from the band, not the queue).
  const getTotalScenarios = () => {
    if (!isFormValid()) return 0;
    if (strikeMode === 'target') return selectedExpirations.length;
    return selectedExpirations.length * selectedStrikes.length;
  };

  return (
    <>
      <div className="pb-0">
        {/* Section 1: Tickers */}
        <AccordionFormSection
          title="Select 1 Ticker"
          icon=""
          summary={selectedTickers.length > 0 ? selectedTickers.map(t => t.value).join(', ') : ''}
          isExpanded={expandedSections.includes('tickers')}
          isDisabled={isSectionDisabled('tickers')}
          isCompleted={completedSections.has('tickers')}
          onToggle={() => handleToggle('tickers')}
          isMobile={isMobile}
          dataTour="tickers-section"
        >
          <MultiSelectTicker
            value={selectedTickers}
            onChange={(tickers) => {
              const capped = capToOneTicker(tickers);
              setSelectedTickers(capped);
              if (capped.length > 0) {
                setCompletedSections(prev => new Set(prev).add('tickers'));
              }
            }}
            placeholder="Search and select one ticker (e.g., SPY)..."
            controlBackground="#1f2937"
          />
        </AccordionFormSection>

        {/* Section 2: Direction */}
        <AccordionFormSection
          title="Market Direction"
          icon=""
          summary={direction ? {
            'buy_call': 'Bullish (Buy Calls)',
            'buy_put': 'Bearish (Buy Puts)',
            'sell_put': 'Neutral/Bullish (Sell Puts)',
            'sell_call': 'Neutral/Bearish (Sell Calls)'
          }[direction] || '' : ''}
          isExpanded={expandedSections.includes('direction')}
          isDisabled={isSectionDisabled('direction')}
          isCompleted={completedSections.has('direction')}
          onToggle={() => handleToggle('direction')}
          isMobile={isMobile}
          dataTour="direction-section"
        >
          <p className="section-helper-text mb-3">Select your market outlook and position type</p>
          <div className="direction-grid grid-cols-2 gap-3">
            {/* Row 1: Buy options */}
            <button
              type="button"
              onClick={() => {
                setDirection('buy_call');
                setCompletedSections(prev => new Set(prev).add('direction'));
              }}
              className={`${cardToggleTemplate.cssClasses.directionBase} ${direction === 'buy_call' ? cardToggleTemplate.cssClasses.selected : ''}`}
            >
              <span className={cardToggleTemplate.cssClasses.directionTitle}>Bullish</span>
              <span className={cardToggleTemplate.cssClasses.directionSubtitle}>Buy Calls</span>
            </button>
            <button
              type="button"
              onClick={() => {
                setDirection('buy_put');
                setCompletedSections(prev => new Set(prev).add('direction'));
              }}
              className={`${cardToggleTemplate.cssClasses.directionBase} ${direction === 'buy_put' ? cardToggleTemplate.cssClasses.selected : ''}`}
            >
              <span className={cardToggleTemplate.cssClasses.directionTitle}>Bearish</span>
              <span className={cardToggleTemplate.cssClasses.directionSubtitle}>Buy Puts</span>
            </button>

            {/* Row 2: Sell options */}
            <button
              type="button"
              onClick={() => {
                setDirection('sell_put');
                setCompletedSections(prev => new Set(prev).add('direction'));
              }}
              className={`${cardToggleTemplate.cssClasses.directionBase} ${direction === 'sell_put' ? cardToggleTemplate.cssClasses.selected : ''}`}
              title="Cash-secured put: Collect premium if stock stays above strike"
            >
              <span className={cardToggleTemplate.cssClasses.directionTitle}>Neutral/Bullish</span>
              <span className={cardToggleTemplate.cssClasses.directionSubtitle}>Sell Puts</span>
              <span className="text-xs text-gray-400 mt-1">Cash-Secured</span>
            </button>
            <button
              type="button"
              onClick={() => {
                setDirection('sell_call');
                setCompletedSections(prev => new Set(prev).add('direction'));
              }}
              className={`${cardToggleTemplate.cssClasses.directionBase} ${direction === 'sell_call' ? cardToggleTemplate.cssClasses.selected : ''} ${direction !== 'sell_call' ? 'border-orange-500/30' : ''}`}
              title="WARNING: Naked calls have unlimited loss potential. Only use if you fully understand the risks."
            >
              <span className={cardToggleTemplate.cssClasses.directionTitle}>Neutral/Bearish</span>
              <span className={cardToggleTemplate.cssClasses.directionSubtitle}>Sell Calls</span>
              <span className="text-xs text-orange-400 mt-1 flex items-center gap-1">
                <span>⚠️</span> High Risk
              </span>
            </button>
          </div>
        </AccordionFormSection>

        {/* Section 3: Expiration Window */}
        <AccordionFormSection
          title="Expiration Window"
          icon=""
          summary={getExpirationSummary()}
          isExpanded={expandedSections.includes('expiration')}
          isDisabled={isSectionDisabled('expiration')}
          isCompleted={completedSections.has('expiration')}
          onToggle={() => handleToggle('expiration')}
          isMobile={isMobile}
          dataTour="expiration-section"
        >
          <p className="section-helper-text">Select how far out the option expires (short-dated bands)</p>
          <div className="intermediate-preset-grid">
            {Object.entries(intradayPresets.expiration).map(([key, preset]) => (
              <IntermediatePresetCard
                key={key}
                presetKey={key}
                label={preset.label}
                description={preset.description}
                tooltip={preset.tooltip}
                isSelected={selectedExpirations.includes(key)}
                onClick={() => {
                  toggleSelection(selectedExpirations, setSelectedExpirations, key);
                  setCompletedSections(prev => new Set(prev).add('expiration'));
                }}
              />
            ))}
          </div>
        </AccordionFormSection>

        {/* Section 4: Strike Positioning */}
        <AccordionFormSection
          title="Strike Positioning"
          icon=""
          summary={getStrikeSummary()}
          isExpanded={expandedSections.includes('strike')}
          isDisabled={isSectionDisabled('strike')}
          isCompleted={completedSections.has('strike')}
          onToggle={() => handleToggle('strike')}
          isMobile={isMobile}
          dataTour="strike-section"
        >
          {/* Sub-mode toggle: moneyness bands vs. an absolute target price */}
          <div className="flex gap-1 mb-3" data-tour="strike-mode-toggle">
            {[['bands', '% ITM/OTM'], ['target', 'Target Price']].map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setStrikeMode(key)}
                className={`px-3 py-1.5 text-xs rounded transition-colors ${
                  strikeMode === key
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {strikeMode === 'target' ? (
            <TargetPriceStrikeSection
              ticker={activeTicker}
              currentPrice={currentPrice}
              priceLoading={priceLoading}
              priceError={priceError}
              onRetryPrice={handleRetryPrice}
              targetPrice={targetPrice}
              onTargetPriceChange={(value) => {
                setTargetPrice(value);
                setCompletedSections(prev => new Set(prev).add('strike'));
              }}
              bufferPct={bufferPct}
              onBufferPctChange={setBufferPct}
              isMobile={isMobile}
            />
          ) : (
            <>
              <p className="section-helper-text">How close to the money? Fine-grained for short-dated moves.</p>
              <div className="intermediate-preset-grid">
                {Object.entries(intradayPresets.strike).map(([key, preset]) => (
                  <IntermediatePresetCard
                    key={key}
                    presetKey={key}
                    label={preset.label}
                    description={getAdjustedStrikeDescription(preset.description)}
                    tooltip={preset.tooltip}
                    isSelected={selectedStrikes.includes(key)}
                    onClick={() => {
                      toggleSelection(selectedStrikes, setSelectedStrikes, key);
                      setCompletedSections(prev => new Set(prev).add('strike'));
                    }}
                  />
                ))}
              </div>
            </>
          )}
        </AccordionFormSection>

      </div>

      {/* Action Buttons — Run / Add to Queue / Save */}
      {!hideButtons && (
        <div className={isMobile ? 'fixed-bottom-button' : 'inline-button'} data-tour="form-actions">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleRunNow}
              disabled={!isFormValid() || loading}
              className={`${actionButtonTemplate.cssClasses.base} ${actionButtonTemplate.cssClasses.runSolid} flex-1 ${!isFormValid() ? actionButtonTemplate.cssClasses.disabled : ''}`}
              style={{ position: 'relative' }}
              title="Run these scenarios immediately"
            >
              {loading ? (
                <span className="spinner"></span>
              ) : (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <Play size={14} style={{ fill: 'currentColor' }} />
                  {getTotalScenarios() > 1
                    ? `Run ${getTotalScenarios()} Scenarios`
                    : 'Run Scenario'}
                </span>
              )}
              {!isFormValid() && (
                <span className={actionButtonTemplate.cssClasses.tooltip}>
                  {getDisabledTooltipMessage()}
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={!isFormValid() || loading}
              className={`${actionButtonTemplate.cssClasses.base} flex-1 ${!isFormValid() ? actionButtonTemplate.cssClasses.disabled : ''}`}
              title="Add these scenarios to the queue without running"
            >
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span>
                  {getTotalScenarios() > 1
                    ? `+ Add ${getTotalScenarios()} to Queue`
                    : '+ Add to Queue'}
                </span>
                {queueCount > 0 && (
                  <span className="queue-badge-inline">{queueCount}</span>
                )}
              </span>
            </button>

            <button
              type="button"
              onClick={async () => {
                const scenarios = buildScenarios();
                if (scenarios.length > 0 && onSaveScenario) {
                  for (const scenario of scenarios) {
                    const success = await onSaveScenario(scenario);
                    if (!success) break;
                  }
                }
              }}
              disabled={!isFormValid() || loading}
              className={`${actionButtonTemplate.cssClasses.base} ${actionButtonTemplate.cssClasses.success}`}
              title="Save this configuration for later use"
            >
              Save as preset
            </button>
          </div>
        </div>
      )}
    </>
  );
});

export default ZeroDTEForm;
